import { useState } from "react";
import api, { formatApiError } from "../../lib/api";
import { toast } from "sonner";
import { PageHeader, PrimaryButton, Modal, Field, TextInput, Select, useFetch, StatusPill } from "../../components/crm/ui";
import { Plus, Trash2, Image as ImageIcon } from "lucide-react";
import { FLYER_LANGS } from "../../lib/flyerI18n";

const EMPTY = { title: "", language: "", category: "", file: null };

export default function Campaigns() {
  const { data, reload } = useFetch("/campaigns");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  function startCreate() { setForm(EMPTY); setOpen(true); }

  async function save(e) {
    e.preventDefault();
    if (!form.title.trim() || !form.file) { toast.error("Title and image are required"); return; }
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append("title", form.title);
      if (form.language) fd.append("language", form.language);
      if (form.category) fd.append("category", form.category);
      fd.append("file", form.file);
      await api.post("/campaigns", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Creative published");
      setOpen(false);
      reload();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setSaving(false);
    }
  }

  async function toggleActive(c) {
    await api.put(`/campaigns/${c.id}`, null, { params: { is_active: !c.is_active } });
    reload();
  }

  async function remove(c) {
    if (!window.confirm(`Delete "${c.title}"?`)) return;
    await api.delete(`/campaigns/${c.id}`);
    toast.success("Deleted");
    reload();
  }

  return (
    <div data-testid="campaigns-page">
      <PageHeader
        overline="Solar Consultant Portal"
        title="Campaign Creatives"
        action={<PrimaryButton onClick={startCreate} testid="create-campaign-btn"><Plus className="w-4 h-4" /> New Creative</PrimaryButton>}
      />
      <p className="text-sm text-[#4A5568] mb-4">Uploaded creatives appear in every Solar Consultant's "Marketing kit → Creatives" tab, with their personal referral QR overlaid automatically in-app.</p>

      <div className="grid grid-cols-4 gap-4">
        {data.map((c) => (
          <div key={c.id} className="border border-[#E2E4DC] bg-white overflow-hidden" data-testid={`campaign-row-${c.id}`}>
            <div className="aspect-square bg-[#F4F5F0]">
              <img
                src={`${process.env.REACT_APP_BACKEND_URL}/api/campaigns/image/${c.image_path}`}
                alt={c.title}
                className="w-full h-full object-cover"
              />
            </div>
            <div className="p-3">
              <div className="text-sm font-medium truncate">{c.title}</div>
              <div className="flex items-center gap-1.5 mt-1.5">
                <StatusPill value={c.is_active ? "active" : "inactive"} tone={c.is_active ? "success" : "muted"} />
                {c.language && <StatusPill value={c.language} />}
              </div>
              <div className="flex items-center gap-2 mt-2.5">
                <button onClick={() => toggleActive(c)} data-testid={`campaign-toggle-${c.id}`} className="text-[10px] font-mono uppercase tracking-widest text-[#1F4E2C] hover:underline">
                  {c.is_active ? "Deactivate" : "Activate"}
                </button>
                <button onClick={() => remove(c)} data-testid={`campaign-delete-${c.id}`} className="p-1 text-red-500 hover:text-red-700 ml-auto"><Trash2 className="w-3.5 h-3.5" /></button>
              </div>
            </div>
          </div>
        ))}
        {data.length === 0 && (
          <div className="col-span-4 border border-[#E2E4DC] bg-white py-14 text-center">
            <ImageIcon className="w-8 h-8 mx-auto text-[#94A3B8]" />
            <div className="text-sm text-[#4A5568] mt-3">No creatives uploaded yet.</div>
          </div>
        )}
      </div>

      <Modal open={open} onClose={() => setOpen(false)} title="New Campaign Creative">
        <form onSubmit={save} className="space-y-4">
          <Field label="Title *">
            <TextInput value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="campaign-title-input" required />
          </Field>
          <Field label="Language (blank = shown to every SC)">
            <Select value={form.language} onChange={(e) => setForm({ ...form, language: e.target.value })} data-testid="campaign-language-select">
              <option value="">All languages</option>
              {FLYER_LANGS.map((l) => <option key={l.code} value={l.code}>{l.label}</option>)}
            </Select>
          </Field>
          <Field label="Category (optional)">
            <TextInput value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} placeholder="e.g. festival, subsidy, testimonial" data-testid="campaign-category-input" />
          </Field>
          <Field label="Image *">
            <input
              type="file"
              accept="image/jpeg,image/jpg,image/png,image/webp"
              onChange={(e) => setForm({ ...form, file: e.target.files?.[0] || null })}
              data-testid="campaign-file-input"
              className="w-full text-sm"
            />
          </Field>
          <PrimaryButton type="submit" testid="campaign-save-btn" disabled={saving}>{saving ? "Uploading…" : "Publish creative"}</PrimaryButton>
        </form>
      </Modal>
    </div>
  );
}
